import { FC, useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useLiveQuery } from "dexie-react-hooks";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
  MDSTextColor,
  MDSListBox,
  MDSCheckbox,
} from "@/mds";
import { useAppStore } from "@/store";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import {
  formatDateString,
  generateRelativePrepositionAdverbialDateString,
  generateShortDateString,
} from "@/domains/date/date";
import { DataExportObservable } from "@/store/data-exports/DataExportObservable";
import { DateTime } from "luxon";
import { api } from "@/modules/api";
import { fileModule } from "@/modules/file";

const handleDownloadExport = async (dataExport: DataExportObservable) => {
  const response = await api.get("/v2/data-exports/{data_export_id}/download-url", {
    params: { path: { data_export_id: dataExport.id } },
  });

  if (response.data?.url) {
    fileModule.downloadFileFromUrlDirectly({
      fileName: `mem-export-${generateShortDateString(DateTime.fromJSDate(dataExport.startedAt))}.zip`,
      downloadUrl: response.data.url,
      inTab: false,
    });
  }
};

function buildExportListItems(exports: DataExportObservable[]) {
  return exports.map(dataExport => {
    const exportDate = DateTime.fromJSDate(dataExport.startedAt);

    if (dataExport.isRunning) {
      return {
        id: dataExport.id,
        children: (
          <StyledExportItemContent>
            <MDSIcon kind={MDSIconKind.Spinner} />
            <MDSText variant={MDSTextVariant.TextS}>
              Export started {generateRelativePrepositionAdverbialDateString(exportDate)}
            </MDSText>
          </StyledExportItemContent>
        ),
      };
    }

    return {
      id: dataExport.id,
      children: (
        <StyledExportItemContent>
          <MDSIcon
            kind={dataExport.didFail ? MDSIconKind.Danger : MDSIconKind.CheckCircleSolid}
            color={dataExport.didFail ? MDSIconColor.Danger : MDSIconColor.Positive}
          />
          <MDSText variant={MDSTextVariant.TextS}>
            {dataExport.didFail
              ? "Export failed"
              : `Export on ${formatDateString(exportDate, "LLLL d, yyyy")}`}
          </MDSText>
        </StyledExportItemContent>
      ),
      rightAddon: (
        <MDSButton
          onClick={() => void handleDownloadExport(dataExport)}
          isDisabled={dataExport.didFail}
        >
          Download
        </MDSButton>
      ),
    };
  });
}

export const SettingsExportsModalPage: FC = observer(() => {
  const { store } = useAppStore();
  const [includeTranscripts, setIncludeTranscripts] = useState(true);
  const [includeRecordings, setIncludeRecordings] = useState(false);
  const voiceCapability = useQuery({
    queryKey: ["export-voice-capability", store.spaceAccounts.myPersonalSpaceAccountId],
    queryFn: async () => {
      const { data, error } = await api.get("/v2/data-exports/supports-voice");
      if (error || typeof data !== "boolean") throw new Error("Voice options unavailable");
      return data;
    },
    staleTime: 60_000,
    retry: false,
  });
  const exportIsRunning = store.dataExports.isExportInProgress;
  const exportsAreReady = store.dataExports.isReady;

  const noteCount = useLiveQuery(
    () => store.notes.localTable.where("is_available").equals(1).count(),
    [],
    0
  );

  const collectionCount = useLiveQuery(
    () => store.collections.localTable.where("is_available").equals(1).count(),
    [],
    0
  );

  const handleExportClick = useCallback(async () => {
    trackEvent(TrackedEvent.ExportCreate);
    await store.dataExports.createExport(
      voiceCapability.data
        ? {
            include_transcripts: includeTranscripts,
            include_recordings: includeRecordings,
          }
        : undefined
    );
  }, [store.dataExports, voiceCapability.data, includeTranscripts, includeRecordings]);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="Account" title="Exports" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledSectionLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            YOUR DATA
          </StyledSectionLabel>
          <StyledDataCounts>
            <StyledDataColumn>
              <MDSText variant={MDSTextVariant.HeaderXl}>{noteCount}</MDSText>
              <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                Notes
              </MDSText>
            </StyledDataColumn>
            <StyledDataColumn>
              <MDSText variant={MDSTextVariant.HeaderXl}>{collectionCount}</MDSText>
              <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                Collections
              </MDSText>
            </StyledDataColumn>
          </StyledDataCounts>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <StyledSectionLabel variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            EXPORT YOUR DATA
          </StyledSectionLabel>
          <StyledExportDescription>
            <MDSText variant={MDSTextVariant.ParagraphS}>
              Export your notes and collections as Markdown files. How it works:
            </MDSText>
            <StyledList>
              <li>Your individual notes will each become a separate Markdown file</li>
              <li>
                Collections will become Markdown files that include all notes from that collection
              </li>
              <li>
                Any images or attachments in your notes will be saved in an &quot;attachments&quot;
                folder
              </li>
              <li>Everything will be packaged in a zip file ready for download</li>
            </StyledList>
          </StyledExportDescription>
          {voiceCapability.data && (
            <div>
              <MDSCheckbox isSelected={includeTranscripts} onChange={setIncludeTranscripts}>
                Include transcripts
              </MDSCheckbox>
              <MDSCheckbox isSelected={includeRecordings} onChange={setIncludeRecordings}>
                Include audio recordings
              </MDSCheckbox>
              <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                Audio recordings can make the ZIP much larger and take longer to export.
              </MDSText>
            </div>
          )}
          {voiceCapability.data === undefined && (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              You can start an export with the default options: transcripts where available, without
              audio recordings.
            </MDSText>
          )}
          {voiceCapability.isError && (
            <MDSButton onClick={() => void voiceCapability.refetch()}>
              Retry loading export options
            </MDSButton>
          )}
          <StyledExportButton
            onClick={handleExportClick}
            isDisabled={!exportsAreReady || exportIsRunning}
          >
            <MDSIcon kind={MDSIconKind.ArrowDownToLine} slot={MDSButtonSlotName.LeftIcon} />
            Export all notes and collections
          </StyledExportButton>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <StyledSectionLabel
            variant={MDSTextVariant.TextXs}
            weight={MDSTextWeight.SemiBold}
            style={{ marginBottom: 20 }}
          >
            YOUR EXPORTS
          </StyledSectionLabel>
          {!exportsAreReady ? (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Tertiary}>
              Loading exports…
            </MDSText>
          ) : store.dataExports.length === 0 ? (
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Tertiary}>
              Your export history will appear here
            </MDSText>
          ) : (
            <MDSListBox items={buildExportListItems(store.dataExports.getLatestExports(3))} />
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledSectionLabel = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const StyledDataCounts = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const StyledDataColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledExportDescription = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-s"]};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const StyledList = styled.ul`
  margin: 0;
  margin-top: ${({ theme }) => theme.spacing[2]};
  padding-left: ${({ theme }) => theme.spacing[5]};
`;

const StyledExportButton = styled(MDSButton)`
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const StyledExportItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;
